import React, { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import AppLayout from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useTenantCurrency } from "@/hooks/use-tenant";
// import { Project } from "@/types";

export default function NewClaimPage() {
  const { projectId } = useParams();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currency: tenantCurrency } = useTenantCurrency();
  
  // Fetch project details for breadcrumbs
  // const { } = useQuery<Project>({
  //   queryKey: [`/api/projects/${projectId}`],
  //   enabled: !!projectId,
  // });
  
  // Create claim mutation
  const createClaimMutation = useMutation({
    mutationFn: async () => {
      // Create a payload with all required fields using default values
      // const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const payload = {
        projectId: projectId,
        status: "Acknowledgment", // Initial status for new claims - starts at Acknowledgment
        claimType: "Fixed Price", // Default claim type (will be updated later based on contract)
        claimHeadline: "Pending document upload and extraction", // Placeholder description
        claimAmount: 0, // Default value, will be updated later
        claimAmountCurrency: tenantCurrency, // Default to tenant currency
        claimIdentifier: `CLM-${Date.now().toString().substring(6)}` // Auto-generated identifier
      };
      
      const res = await apiRequest("POST", `/api/projects/${projectId}/claims`, payload);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}/claims`] });
      toast({
        title: "Claim registered",
        description: "You can now upload documents to extract the claim details.",
      });
      // Navigate to the newly created claim
      navigate(`/claims/${data.claimId}`);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to register the claim",
        variant: "destructive",
      });
      setIsSubmitting(false);
    },
  });
  
  // Form submission handler - no form data, just trigger the mutation
  const handleCreateClaim = () => {
    setIsSubmitting(true);
    createClaimMutation.mutate();
  };
  
  // Go back to project page
  const goBack = () => {
    navigate(`/projects/${projectId}`);
  };
  
  return (
    <AppLayout>
      <div className="py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8">
          {/* Back button */}
          <div className="mb-6">
            <Button variant="ghost" size="sm" onClick={goBack}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Project
            </Button>
          </div>
          
          {/* Page Title */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold">Register New Claim</h1>
            <p className="text-neutral-600 mt-1">
              Register a claim and then upload documents to extract the details.
            </p>
          </div>
          
          {/* One-Click Claim Registration */}
          <Card>
            <CardHeader>
              <CardTitle>Claim Registration</CardTitle>
              <CardDescription>
                Click below to register a new claim. You will be able to upload documents and extract all details in the next step.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                  <h3 className="font-medium text-primary mb-2">How it works</h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>A claim will be registered with a unique identifier.</li>
                    <li>You'll be redirected to the claim detail page.</li>
                    <li>Upload the claim documents on the detail page.</li>
                    <li>The system will help extract details from documents.</li>
                  </ul>
                </div>
                
                <div className="flex justify-end gap-4 pt-2">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={goBack}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateClaim} 
                    disabled={isSubmitting} 
                    className="px-6"
                  >
                    {isSubmitting ? "Registering..." : "Register Claim"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}